import { useMutation } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import {
  ComparisonEnvironment,
  ShadowTestResponse,
  Stage,
} from "../lusca-search-api/generated-src";
import { ShadowTestApiFactory } from "../lusca-search-api/LuscaSearchApiFactory";

interface ShadowTestParams {
  stage: Stage;
  marketplace: string;
  glProductGroupId: string;
  transactionIds: string[];
  countryCode: string;
  auid?: string;
  subscriptionId?: string;
  periodNumber?: string;
  deliveryPacketItemId?: string;
  digitalOrderID?: string;
  financeTagAuthReferenceId?: string;
  comparisonEnvironment?: ComparisonEnvironment;
}

interface ShadowTestResult {
  isLoading: boolean;
  error: Error | null;
  transactionsCount?: number;
  trigger: (params: ShadowTestParams) => void;
}

export default function useShadowTest(): ShadowTestResult {
  const shadowTestApi = ShadowTestApiFactory();

  const { mutate, isLoading, error, data } = useMutation<
    ShadowTestResponse,
    Error,
    ShadowTestParams
  >(
    async ({
      stage,
      marketplace,
      glProductGroupId,
      transactionIds,
      countryCode,
      auid,
      subscriptionId,
      periodNumber,
      deliveryPacketItemId,
      digitalOrderID,
      financeTagAuthReferenceId,
      comparisonEnvironment,
    }) => {
      const { data: responseData }: AxiosResponse<ShadowTestResponse> =
        await shadowTestApi.createShadowTest(stage, {
          marketplace,
          glProductGroupId,
          transactionIds,
          countryCode,
          auid: auid || undefined,
          subscriptionId: subscriptionId || undefined,
          periodNumber: periodNumber || undefined,
          deliveryPacketItemId: deliveryPacketItemId || undefined,
          digitalOrderID: digitalOrderID || undefined,
          financeTagAuthReferenceId: financeTagAuthReferenceId || undefined,
          ...(comparisonEnvironment ? { comparisonEnvironment } : {}),
        });
      return responseData;
    }
  );

  const trigger = (params: ShadowTestParams) => {
    mutate(params);
  };

  return {
    isLoading,
    error: error || null,
    transactionsCount: data?.transactionsCount,
    trigger,
  };
}
